import { QueryRender } from "@/components/helpers/QueryRender"
import { useAuth } from "@/hooks/useAuth"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, useFieldArray } from "react-hook-form"
import { z } from "zod"
import { useCreateSchoolYear } from "@/entities/schoolYear/useSchoolYear"
import { useUpdateGlobalSettings } from "@/entities/globalSettings/useGlobalSettings"
import { schoolYearSchema } from "@/entities/schoolYear/schoolYear"
import { isDevEnv } from "@/config/env"
import { useProcess } from "@/contexts/ProcessContext"
import { parseISO, startOfDay, endOfDay } from "date-fns"
import { getNow } from "@/contexts/TimeMachineContext"
import NouvelleAnneeSteps from "./NouvelleAnneeSteps"
import { useNavigate } from "react-router-dom"

const formSchema = schoolYearSchema
   .extend({
      // vacation_periods: z.array(z.object({
      // 	name: z.string().min(1, "Le nom est requis"),
      // 	start_date: z.string().min(1, "La date de début est requise"),
      // 	end_date: z.string().min(1, "La date de fin est requise")
      // }))
   })
   .omit({ created_at: true, updated_at: true })

type FormValues = z.infer<typeof formSchema>

export default function NouvelleAnneeConfigurerAnnee() {
   const navigate = useNavigate()
   const { user, ...query } = useAuth()
   const createSchoolYear = useCreateSchoolYear()
   const updateGlobalSettings = useUpdateGlobalSettings()
   const { startProcess } = useProcess()

   const form = useForm<FormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: isDevEnv
         ? {
              name: "2024-2025",
              start_date: "2024-09-01",
              end_date: "2025-07-05",
              vacation_periods: [
                 { name: "Toussaint", start_date: "2024-10-19", end_date: "2024-11-04" },
                 { name: "Noël", start_date: "2024-12-21", end_date: "2025-01-05" },
                 { name: "Hiver", start_date: "2025-02-09", end_date: "2025-02-25" },
              ],
           }
         : {
              name: `${getNow().getFullYear()}-${getNow().getFullYear() + 1}`,
              start_date: "",
              end_date: "",
              is_current: true,
              vacation_periods: [
                 { name: "Toussaint", start_date: "", end_date: "" },
                 { name: "Noël", start_date: "", end_date: "" },
                 { name: "Hiver", start_date: "", end_date: "" },
              ],
           },
   })

   const { fields, append, remove } = useFieldArray({
      control: form.control,
      name: "vacation_periods",
   })

   const { register, handleSubmit, formState } = form

   const onSubmit = async (data: FormValues) => {
      startProcess(async () => {
         const schoolYearId = await createSchoolYear({
            ...data,
            start_date: startOfDay(data.start_date),
            end_date: endOfDay(data.end_date),
            vacation_periods: data.vacation_periods.map((period) => ({
               ...period,
               start_date: startOfDay(period.start_date),
               end_date: endOfDay(period.end_date),
            })),
         },{
            quickMode: true,
         })

         await updateGlobalSettings("main", {
            current_schoolyear_id: schoolYearId,
         },{
            quickMode: true,
         })

         form.reset()

         await navigate("/admin/nouvelle-annee/configurer-groupes")
      })
   }

   return (
      <div className="container mx-auto py-6">
         <NouvelleAnneeSteps step={1} />

         <QueryRender query={query} data={user}>
            {() => (
               <div className="bg-white shadow rounded-lg p-6">
                  <h1 className="text-2xl font-bold mb-6">Nouvelle année scolaire</h1>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                     <div>
                        <label className="block text-sm font-medium text-gray-700">
                           Nom de l'année scolaire
                        </label>
                        <input
                           type="text"
                           {...register("name")}
                           className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                           placeholder="2023-2024"
                        />
                        {formState.errors.name && (
                           <p className="mt-1 text-sm text-red-600">
                              {formState.errors.name.message}
                           </p>
                        )}
                     </div>

                     <div className="grid grid-cols-2 gap-4">
                        <div>
                           <label className="block text-sm font-medium text-gray-700">
                              Date de début
                           </label>
                           <input
                              type="date"
                              {...register("start_date", {
                                 setValueAs: (value: string | Date) => {
                                    if (!value) return undefined
                                    if (value instanceof Date) return startOfDay(value)
                                    return startOfDay(parseISO(value))
                                 },
                              })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                           />
                           {formState.errors.start_date && (
                              <p className="mt-1 text-sm text-red-600">
                                 {formState.errors.start_date.message}
                              </p>
                           )}
                        </div>

                        <div>
                           <label className="block text-sm font-medium text-gray-700">
                              Date de fin
                           </label>
                           <input
                              type="date"
                              {...register("end_date", {
                                 setValueAs: (value: string | Date) => {
                                    if (!value) return undefined
                                    if (value instanceof Date) return endOfDay(value)
                                    return endOfDay(parseISO(value))
                                 },
                              })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                           />
                           {formState.errors.end_date && (
                              <p className="mt-1 text-sm text-red-600">
                                 {formState.errors.end_date.message}
                              </p>
                           )}
                        </div>
                     </div>

                     <div className="space-y-4">
                        <h2 className="text-lg font-medium">Périodes de vacances</h2>
                        {fields.map((field, index) => (
                           <div key={field.id} className="flex items-center gap-4">
                              <div className="flex-1 grid gap-4 p-4 border rounded-lg">
                                 <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                       Nom de la période
                                    </label>
                                    <input
                                       type="text"
                                       {...register(`vacation_periods.${index}.name`)}
                                       className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                                       placeholder="Toussaint"
                                    />
                                    {formState.errors.vacation_periods?.[index]?.name && (
                                       <p className="mt-1 text-sm text-red-600">
                                          {formState.errors.vacation_periods[index]?.name?.message}
                                       </p>
                                    )}
                                 </div>

                                 <div className="grid grid-cols-2 gap-4">
                                    <div>
                                       <label className="block text-sm font-medium text-gray-700">
                                          Date de début
                                       </label>
                                       <input
                                          type="date"
                                          {...register(`vacation_periods.${index}.start_date`, {
                                             setValueAs: (value: string | Date) => {
                                                if (!value) return undefined
                                                if (value instanceof Date) return startOfDay(value)
                                                return startOfDay(parseISO(value))
                                             },
                                          })}
                                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                                       />
                                       {formState.errors.vacation_periods?.[index]?.start_date && (
                                          <p className="mt-1 text-sm text-red-600">
                                             {
                                                formState.errors.vacation_periods[index]?.start_date
                                                   ?.message
                                             }
                                          </p>
                                       )}
                                    </div>

                                    <div>
                                       <label className="block text-sm font-medium text-gray-700">
                                          Date de fin
                                       </label>
                                       <input
                                          type="date"
                                          {...register(`vacation_periods.${index}.end_date`, {
                                             setValueAs: (value: string | Date) => {
                                                if (!value) return undefined
                                                if (value instanceof Date) return endOfDay(value)
                                                return endOfDay(parseISO(value))
                                             },
                                          })}
                                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                                       />
                                       {formState.errors.vacation_periods?.[index]?.end_date && (
                                          <p className="mt-1 text-sm text-red-600">
                                             {
                                                formState.errors.vacation_periods[index]?.end_date
                                                   ?.message
                                             }
                                          </p>
                                       )}
                                    </div>
                                 </div>
                              </div>
                              <button
                                 type="button"
                                 onClick={() => remove(index)}
                                 className="p-2 text-gray-400 hover:text-red-500 focus:outline-none"
                                 title="Supprimer la période"
                              >
                                 <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                 >
                                    <path
                                       fillRule="evenodd"
                                       d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                       clipRule="evenodd"
                                    />
                                 </svg>
                              </button>
                           </div>
                        ))}

                        <button
                           type="button"
                           onClick={() => append({ name: "", start_date: "", end_date: "" })}
                           className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                           Ajouter une période de vacances
                        </button>
                     </div>

                     <div>
                        <button type="submit" className="btn btn-primary">
                           Créer l'année scolaire
                        </button>
                     </div>
                  </form>
               </div>
            )}
         </QueryRender>
      </div>
   )
}
